import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import Btn from '../../../component/button/btn/Btn';
import BtnLanguages from '../../../component/button/btnLanguages/BtnLanguages';
import AIGeneratorButton from '../../../component/aiMessageGenerator/aiGeneratorButton/AIGeneratorButton';
import useMobile from '../../../hooks/useMobile';
import useGlobalShow from '../../../hooks/useGlobalShow';
import type {TShow} from '../../../hooks/useShow';
import {IS_NEW_TEMPLATE} from '../../../config/config';
import {DELIVERY_TYPES} from '../menu/brandingConfig';
import type {DeliveryType, MessageType} from '../../../hooks/useBrandingParams';
import {logGoogleEvent} from '../../../utils/analytics';
import getTicketLinkOptionsList, {ticketLinkOptionsCssStyle} from '../functions/getTicketLinkOptionsList';
import type {TState} from '../../../redux/store.type';
import {op} from '../../../assets/symbols/symbols/Symbols.css';
import {isMaster} from '../../../functions/permission';

interface UseBrandingPageActionsParams {
    messageType: MessageType;
    deliveryType: DeliveryType;
    eventWhatsAppStatus: boolean;
    eventEid: number | string;
    aiGeneratorShow: TShow;
}

export function useBrandingPageActions({
    messageType,
    deliveryType,
    eventWhatsAppStatus,
    eventEid,
    aiGeneratorShow,
}: UseBrandingPageActionsParams) {
    const {t: tWhatsApp} = useTranslation('whatsapp');
    const {t} = useTranslation('branding');
    const isMobile = useMobile();
    const {useNewTicketLink, eventType} = useSelector((s: TState) => {
        const lang = s.branding.currentLang ?? s.event.lang ?? 'he';
        return {
            useNewTicketLink: s.branding.data?.[lang]?.useNewTicketLink ?? s.branding.data?.[lang]?.newTicketLink,
            eventType: s.event.type,
        };
    });
    const trialPopupShow = useGlobalShow('isOpenTrialPopup');
    const trialPopShow = useGlobalShow('isOpenTrialPop');
    const showWhatsAppToolbar = deliveryType === DELIVERY_TYPES.whatsapp && eventWhatsAppStatus;
    const showTicketLinkSwitch = isMaster()
        && eventType === 'ticket'
        && (deliveryType === DELIVERY_TYPES.email || deliveryType === DELIVERY_TYPES.whatsapp);
    const newService = IS_NEW_TEMPLATE(Number(eventEid));

    const openTrial = useCallback(() => {
        trialPopupShow.open();
        trialPopShow.open();
    }, [trialPopupShow, trialPopShow]);

    const openAiGenerator = useCallback(() => {
        if(eventEid) {
            logGoogleEvent('ai_message_generator_open', eventEid, 'eid', {
                popup_name: 'aiMessageGenerator',
                message_type: messageType,
                feature: 'whatsapp',
            });
        }
        aiGeneratorShow.open();
    }, [eventEid, messageType, aiGeneratorShow]);

    const aiGeneratorButtonProps = useMemo(() => ({
        label: tWhatsApp('aiGenerator.triggerLabel'),
        onClick: openAiGenerator,
        'data-testid': 'whatsapp-ai-generator-trigger',
    }), [tWhatsApp, openAiGenerator]);

    const ticketLinkOptionsList = useMemo(
        () => getTicketLinkOptionsList({useNewTicketLink, t}),
        [useNewTicketLink, t],
    );

    const operations = useMemo(() => {
        const buttons: Array<{key: number; Comp: unknown; props?: Record<string, unknown>}> = [];
        if(!isMobile) {
            buttons.push({key: 0, Comp: BtnLanguages});
        }
        if(showWhatsAppToolbar) {
            if(newService) {
                buttons.push({
                    key: 1,
                    Comp: AIGeneratorButton,
                    props: {...aiGeneratorButtonProps, cssStyle: 'margin-inline: var(--space-lg);'},
                });
            }
            buttons.push({
                key: 2,
                Comp: Btn,
                props: {
                    onClick: openTrial,
                    dataCy: 'whatsapp-send-trial-btn',
                    children: tWhatsApp('sendTrial'),
                },
            });
        }
        const result: {
            buttons: typeof buttons;
            optionsList?: ReturnType<typeof getTicketLinkOptionsList>;
            cssStyle?: string;
        } = {buttons};
        if(showTicketLinkSwitch) {
            result.optionsList = ticketLinkOptionsList;
            result.cssStyle = ticketLinkOptionsCssStyle;
        }
        return result;
    }, [isMobile, showWhatsAppToolbar, newService, aiGeneratorButtonProps, openTrial, tWhatsApp, showTicketLinkSwitch, ticketLinkOptionsList]);

    const mobileActions = useMemo(() => {
        const actions: Array<{text: string; onClick?: () => void; icon?: string; list?: Record<string, unknown>}> = [];
        if(showWhatsAppToolbar) {
            actions.push({text: tWhatsApp('sendTrial'), onClick: openTrial});
        }
        if(showTicketLinkSwitch) {
            actions.push({
                text: t('ticketLinkType'),
                icon: op,
                list: {...ticketLinkOptionsList, arr: ticketLinkOptionsList.list},
            });
        }
        return actions;
    }, [showWhatsAppToolbar, showTicketLinkSwitch, tWhatsApp, t, openTrial, ticketLinkOptionsList]);

    return {operations, mobileActions, aiGeneratorButtonProps};
}
